import "antd/dist/antd.css"
import "@/styles/global.css"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { App as AntdApp, theme as antdTheme, ConfigProvider } from "antd"
import { useEffect, useMemo } from "react"
import ReactDOM from "react-dom/client"
import { useShallow } from "zustand/shallow"
import { useResolvedThemeMode } from "@/hooks/useResolvedThemeMode"
import App from "./app"
import { usePreferencesStore } from "./stores/store"

import vi_VN from "antd/locale/vi_VN"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"

dayjs.locale("vi")
dayjs.extend(utc)
dayjs.extend(timezone)

const lightComponents = {
  Layout: {
    headerPadding: "0",
    headerHeight: "auto",
    bodyBg: "#f2f3f5",
  },
  Tree: {
    directoryNodeSelectedBg: "#e6f4ff",
    indentSize: 12,
    directoryNodeSelectedColor: "rgba(0, 0, 0, 0.88)",
  },
  Card: {
    colorBorder: "#e4e7ed",
  },
} as const

const darkComponents = {
  Layout: {
    headerPadding: "0",
    headerHeight: "auto",
  },
  Tree: {
    directoryNodeSelectedBg: "rgba(255, 255, 255, 0.12)",
    indentSize: 12,
    directoryNodeSelectedColor: "rgba(255, 255, 255, 0.85)",
  },
  Card: {},
} as const

const GlobalProvider: React.FC = () => {
  const { colorPrimary, colorError, colorSuccess, colorWarning, radius, themeMode, compact } = usePreferencesStore(
    useShallow((state) => ({
      colorPrimary: state.preferences.theme.colorPrimary,
      colorError: state.preferences.theme.colorError,
      colorSuccess: state.preferences.theme.colorSuccess,
      colorWarning: state.preferences.theme.colorWarning,
      locale: state.preferences.app.locale,
      radius: state.preferences.theme.radius,
      themeMode: state.preferences.theme.mode,
      compact: state.preferences.app.compact,
    }))
  )

  const resolvedColorMode = useResolvedThemeMode(themeMode)
  const isDark = resolvedColorMode === "dark"

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", isDark ? "dark" : "light")
    document.documentElement.style.colorScheme = isDark ? "dark" : "light"
  }, [isDark])

  const algorithm = useMemo(() => {
    const base = isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm
    return compact ? [base, antdTheme.compactAlgorithm] : base
  }, [isDark, compact])

  const mergedTheme = useMemo(
    () => ({
      hashed: false,
      zeroRuntime: true,
      algorithm,
      token: {
        colorPrimary,
        colorError,
        colorSuccess,
        colorWarning,
        borderRadius: radius,
      },
      components: isDark ? darkComponents : lightComponents,
    }),
    [algorithm, colorPrimary, colorError, colorSuccess, colorWarning, radius, isDark]
  )

  return (
    <ConfigProvider theme={mergedTheme} locale={vi_VN}>
      <AntdApp className="h-full">
        <App />
      </AntdApp>
    </ConfigProvider>
  )
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      gcTime: 1000 * 60 * 60 * 3,
      networkMode: "online",
    },
    mutations: {
      networkMode: "online",
    },
  },
})

ReactDOM.createRoot(document.getElementById("root")!).render(
  <QueryClientProvider client={queryClient}>
    <GlobalProvider />
  </QueryClientProvider>
)
